import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue'
  import { useRouter } from 'vue-router/composables'

  import GetStartedPanelLoader from '@/components/loaders/GetStartedPanelLoader.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import ContactMe from '@/components/common/ContactMe.vue'

  import useIcons from '@/composables/useIcons'
  import useI18n from '@/composables/useI18n'
  import useVideo from '@/composables/useVideo'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useShopifyClient from '@/composables/useShopifyClient'

  import { UserModule } from '@/store/modules/user'

  interface Step {
    active: boolean
    completed: boolean
    requiresUser?: boolean
    action: () => void
    getTitle: () => string
  }

  interface StepConfig {
    key: string
    shouldInclude: () => boolean
    createStep: () => Omit<Step, 'active'>
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'GetStartedPanel',
  setup(__props) {

  const router = useRouter()

  const { checkboxSquare, checkedCircle, michaelPortrait, play, expandIcon, collapseIcon } = useIcons()
  const { translateString } = useI18n()
  const { getPosterImage } = useVideo()
  const { hasValidUser, showCompanyOnboardingPopupDetails } = useCommonMixin()
  const {
    course,
    courseBundle,
    nextModuleToStart,
    navigateToCourseContent,
    navigateToModule,
    showStartCourseForFreeModal,
    contactEmail,
    findFreeModule,
  } = useCourse()
  const { handleWebsiteCheckout } = useShopifyClient()

  const isCollapsed = ref(false)

  const loading = computed(() => UserModule.fetchingOnboardingPopupDetails || !course.value)

  const companyOnboardingPopupDetails = computed(() => UserModule.companyOnboardingPopupDetails)

  const introVideoWatched = computed(() => UserModule.getStartedChecklist?.introVideoWatched ?? false)

  const contactMeDescription = computed(() => {
    if (course.value?.purchased) {
      return `<b>E-mail:</b> <a href="mailto:${contactEmail.value}" class="contact-link">${contactEmail.value}</a> ${translateString('or')} <b>Whatsapp:</b> <a href="https://wa.me/493022052308" class="contact-link" target="_blank" rel="noopener noreferrer">030 2205 2308</a>.`
    }
    return translateString('helpDetails.feelFreeToContactMe')
  })

  const stepConfigs: StepConfig[] = [
    {
      key: 'setupAccount',
      shouldInclude: () => true,
      createStep: () => ({
        completed: UserModule.getStartedChecklist?.setUpYourAccount ?? false,
        requiresUser: false,
        action: () => router.push('/login'),
        getTitle: () =>
          translateString('homepage.getStartedChecklist.setUpYourAccount', {
            company: UserModule.currentCompany?.name ?? '',
          }),
      }),
    },
    {
      key: 'introVideo',
      shouldInclude: () => !!companyOnboardingPopupDetails.value,
      createStep: () => ({
        completed: introVideoWatched.value,
        requiresUser: true,
        action: () => showCompanyOnboardingPopupDetails(),
        getTitle: () => translateString('homepage.getStartedChecklist.yourFirstClickIntroVideo'),
      }),
    },
    {
      key: 'readFirstLesson',
      shouldInclude: () => true,
      createStep: () => ({
        completed:
          (UserModule.getStartedChecklist?.read1Lesson ?? false) || (course.value?.numberOfLessonsCompleted ?? 0) >= 1,
        requiresUser: true,
        action: () => handleRouteToNextLesson(),
        getTitle: () => translateString('homepage.getStartedChecklist.browseThroughYourFirstLesson'),
      }),
    },
    {
      key: 'testYourQuizKnowledge',
      shouldInclude: () => !course.value?.purchased && !course.value?.expired,
      createStep: () => ({
        completed: UserModule.getStartedChecklist?.testYourQuizKnowledge ?? false,
        requiresUser: true,
        action: () => handleRouteToGuidedQuiz(),
        getTitle: () => translateString('homepage.getStartedChecklist.testYourQuizKnowledge'),
      }),
    },
    {
      key: 'startExamSimulation',
      shouldInclude: () => !!course.value?.exam,
      createStep: () => ({
        completed: UserModule.getStartedChecklist?.startExamSimulation ?? false,
        requiresUser: true,
        action: () => router.push(`/courses/${course.value!.id}/exam/${course.value!.exam!.id}/start`),
        getTitle: () => translateString('homepage.getStartedChecklist.startExamSimulation'),
      }),
    },
    {
      key: 'purchasePackage',
      shouldInclude: () => true,
      createStep: () => ({
        completed:
          course.value?.purchased ||
          (courseBundle.value?.courses.every((course) => course.purchased) ?? false) ||
          course.value?.expired ||
          false,
        requiresUser: true,
        action: () => handleWebsiteCheckout(course.value!.id),
        getTitle: () => translateString('homepage.getStartedChecklist.findTheLearningPackageThatSuitsYou'),
      }),
    },
  ]

  const steps = computed<Step[]>(() => {
    return stepConfigs
      .filter((config) => config.shouldInclude())
      .map((config) => ({
        active: false,
        ...config.createStep(),
      }))
  })

  const allStepsCompleted = computed(() => steps.value.every((step) => step.completed))

  const nextIncompleteStepIndex = computed(() => steps.value.findIndex((step) => !step.completed))

  const popupPosterImage = computed(() => {
    if (!companyOnboardingPopupDetails.value) return ''

    return (
      companyOnboardingPopupDetails.value.posterImageUrl ??
      getPosterImage(
        companyOnboardingPopupDetails.value.videoUrl ?? '',
        companyOnboardingPopupDetails.value.timestamp ?? 0,
      )
    )
  })

  const nextLesson = computed(() => {
    if (!nextModuleToStart.value) return null

    return nextModuleToStart.value.lessons.find((lesson) => !lesson.completed) || nextModuleToStart.value.lessons[0]
  })

  const updateActiveStep = () => {
    steps.value.forEach((step, index) => {
      step.active = index === nextIncompleteStepIndex.value
    })
  }

  const handleStepClick = (step: Step) => {
    if (step.requiresUser && !hasValidUser.value) {
      router.push('/login')
      return
    }

    if (step.completed) return

    if (step.action) {
      step.action()
    }
  }

  const handleRouteToNextLesson = () => {
    if (course.value?.purchased || course.value?.expired) {
      navigateToCourseContent(nextModuleToStart.value, nextLesson.value, course.value!.id, false)
    } else if (course.value?.trial) {
      navigateToModule(course.value!.id, nextModuleToStart.value, nextLesson.value)
    } else {
      showStartCourseForFreeModal()
    }
  }

  const handleRouteToGuidedQuiz = () => {
    const freeModule = findFreeModule(course.value!.id)
    if (freeModule) {
      router.push(`/courses/${course.value!.id}/module/${freeModule.id}/guided-quiz`)
    }
  }

  const toggleCollapse = (event: MouseEvent) => {
    if (course.value?.purchased) return

    if (isCollapsed.value) {
      isCollapsed.value = false
    } else {
      event.stopPropagation()
      isCollapsed.value = true
    }
  }

  watch(nextIncompleteStepIndex, updateActiveStep, { immediate: true })
  watch(steps, updateActiveStep, { immediate: true })
  watch(
    allStepsCompleted,
    (completed) => {
      if (completed) {
        isCollapsed.value = true
      }
    },
    { immediate: true },
  )

return { __sfc: true,router, checkboxSquare, checkedCircle, michaelPortrait, play, expandIcon, collapseIcon, translateString, getPosterImage, hasValidUser, showCompanyOnboardingPopupDetails, course, courseBundle, nextModuleToStart, navigateToCourseContent, navigateToModule, showStartCourseForFreeModal, contactEmail, findFreeModule, handleWebsiteCheckout, isCollapsed, loading, companyOnboardingPopupDetails, introVideoWatched, contactMeDescription, stepConfigs, steps, allStepsCompleted, nextIncompleteStepIndex, popupPosterImage, nextLesson, updateActiveStep, handleStepClick, handleRouteToNextLesson, handleRouteToGuidedQuiz, toggleCollapse, ref, computed, watch, useRouter, GetStartedPanelLoader, SVGRenderer, ContactMe, useIcons, useI18n, useVideo, useCourse, useCommonMixin, useShopifyClient, UserModule }
}

})